"use client";

import { Skeleton } from "@/components/_ui/skeleton";
import { projectToPixel } from "@/utils/mapboxStatic/mercator";
import { buildSmoothPath } from "@/utils/mapboxStatic/smoothPath";
import type { ScanStop } from "@/utils/publicTracking/scanJourney";
import type { ScanJourney } from "@/utils/publicTracking/useScanJourneyMap";
import { cn } from "@/utils/utils";

interface ScanJourneyMapProps {
  /** Resolved journey from `useScanJourneyMap` (geocoded, cached). */
  journey: ScanJourney;
  /** Floating last-scan / Destination labels. Default false. */
  showLabels?: boolean;
  /** Corner legend. Default false. */
  showLegend?: boolean;
  /** Draw the dashed estimated segment to the destination. Default true. */
  estimated?: boolean;
  className?: string;
}

// Fill/stroke as Tailwind utility classes. `last` uses `fill-current`, which
// inherits from the SVG's parent `text-orange-500` (the brand citrus accent).
const ROLE_FILL_CLASS: Record<ScanStop["kind"], string> = {
  scan: "fill-white",
  last: "fill-current",
  destination: "fill-emerald-600",
};
const ROLE_STROKE_CLASS: Record<ScanStop["kind"], string> = {
  scan: "stroke-muted-foreground",
  last: "stroke-white",
  destination: "stroke-white",
};
const ROLE_RADIUS: Record<ScanStop["kind"], number> = {
  scan: 5,
  last: 8,
  destination: 8,
};
const LABELLED_KINDS = new Set<ScanStop["kind"]>(["last", "destination"]);

function roleLabel(stop: ScanStop): string {
  if (stop.kind === "destination") return "Destination";
  // Short "City, ST" pin label from the facility string (drop ZIP + extras).
  const stripped = stop.label.replace(/\s*\d{5}(-\d{4})?\s*$/, "").trim();
  return stripped.split(/\s+/).slice(0, 2).join(" ") || stop.label;
}

function RouteLegend() {
  return (
    <div className="bg-card/95 absolute bottom-2 left-2 z-10 flex flex-col gap-1 rounded-md border px-2.5 py-2 text-[11px] shadow-sm">
      <span className="flex items-center gap-1.5">
        <span className="size-2 rounded-full bg-orange-500" />
        Last scan
      </span>
      <span className="flex items-center gap-1.5">
        <span className="size-2 rounded-full bg-emerald-600" />
        Destination ZIP
      </span>
    </div>
  );
}

/**
 * A map of a piece's journey: a static Mapbox basemap with an SVG overlay
 * (role-colored markers + traveled solid line + dashed estimated segment),
 * rendered from a resolved `ScanJourney`. Renders a skeleton while
 * resolving and nothing when idle / nothing geocoded.
 */
export function ScanJourneyMap({
  journey,
  showLabels = false,
  showLegend = false,
  estimated = true,
  className,
}: ScanJourneyMapProps) {
  const { status, viewport, mapUrl, stops, width, height } = journey;

  if (status === "idle" || status === "empty") return null;

  const aspectStyle = { aspectRatio: `${width} / ${height}` };

  if (status === "loading" || !viewport || !mapUrl) {
    return (
      <Skeleton
        className={cn("w-full rounded-md", className)}
        style={aspectStyle}
      />
    );
  }

  const projected = stops.map((stop) => ({
    stop,
    px: projectToPixel(stop.point, viewport, width, height),
  }));

  const traveled = projected.filter((p) => p.stop.kind !== "destination");
  const destination = projected.find((p) => p.stop.kind === "destination");
  const last =
    projected.find((p) => p.stop.kind === "last") ??
    traveled[traveled.length - 1];
  const traveledPath = buildSmoothPath(traveled.map((p) => p.px));
  const estimatedPath =
    estimated && destination && last
      ? buildSmoothPath([last.px, destination.px])
      : "";

  return (
    <div
      className={cn(
        "relative w-full overflow-hidden rounded-md border",
        className,
      )}
      style={aspectStyle}
    >
      {/* Static basemap. next/image isn't a fit — the URL is dynamic per IMb
          and Next can't optimize a Mapbox-hosted image. */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={mapUrl}
        alt="Map of this mail piece's scan locations"
        width={width}
        height={height}
        draggable={false}
        className="block h-full w-full object-cover"
      />
      <svg
        className="pointer-events-none absolute inset-0 h-full w-full text-orange-500"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        {estimatedPath ? (
          <path
            d={estimatedPath}
            fill="none"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeDasharray="6 7"
            className="stroke-muted-foreground"
          />
        ) : null}
        {traveledPath ? (
          <>
            <path
              d={traveledPath}
              fill="none"
              stroke="white"
              strokeOpacity={0.75}
              strokeWidth={5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d={traveledPath}
              fill="none"
              stroke="currentColor"
              strokeWidth={3}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </>
        ) : null}
        {projected.map(({ stop, px }, i) => (
          <circle
            key={`${stop.query}-${i}`}
            cx={px.x}
            cy={px.y}
            r={ROLE_RADIUS[stop.kind]}
            className={cn(
              ROLE_FILL_CLASS[stop.kind],
              ROLE_STROKE_CLASS[stop.kind],
            )}
            strokeWidth={2.5}
          />
        ))}
      </svg>
      {showLabels
        ? projected
            .filter((p) => LABELLED_KINDS.has(p.stop.kind))
            .map(({ stop, px }, i) => (
              <div
                key={`label-${stop.query}-${i}`}
                className="bg-card pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-[170%] rounded-md border px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap shadow-sm"
                style={{
                  left: `${(px.x / width) * 100}%`,
                  top: `${(px.y / height) * 100}%`,
                }}
              >
                {roleLabel(stop)}
              </div>
            ))
        : null}
      {showLegend ? <RouteLegend /> : null}
    </div>
  );
}
